import type { Dimensions } from "../canvas/canvasTypes";
import type { ComicPanel } from "../comic/comicTypes";
import type { FillCommand, RasterSelectionCommand, ShapeCommand } from "../history/historyTypes";
import { applyRasterSelectionCommandToContext } from "../selection/RasterSelectionCommand";
import { ImmutableRasterPixelBuffer } from "../selection/RasterPixelBuffer";
import { selectionLiveRegionBytes } from "../selection/selectionMemory";
import type { IntegerRect } from "../selection/selectionTypes";
import type { PersistedLayerState, StoredRasterLayerBlob } from "./layerTypes";
import {
  assertAggregateRasterMemoryWithinBudget,
  canAllocateRasterSurface,
  canBeginRasterEditWithPreview,
  canBeginRasterStrokeWithPreview,
  canUseRasterSurfaceBudget,
  canUseRasterSurfaceBudgetWithExtraBytes,
} from "./layerMemory";
import { RasterLayerSurface, type EncodedRasterLayer } from "./RasterLayerSurface";
import { renderShape } from "../shapes/ShapeRenderer";
import { applyBoundedFloodFill } from "../fill/floodFill";
import { canApplyBoundedFill } from "../fill/fillMemory";
import type { FillResult, RgbaTuple } from "../fill/fillTypes";
import { hexToRgba } from "../colour/colourMath";

export class LayerRasterStore {
  private readonly surfaces = new Map<string, RasterLayerSurface>();

  public constructor(
    private readonly size: Dimensions,
    layers: ReadonlyArray<PersistedLayerState>,
  ) {
    this.syncLayers(layers);
  }

  public syncLayers(layers: ReadonlyArray<PersistedLayerState>): void {
    const rasterIds = new Set(
      layers.filter((layer) => layer.type === "raster").map((layer) => layer.id),
    );
    for (const [id, surface] of this.surfaces) {
      if (!rasterIds.has(id)) {
        surface.destroy();
        this.surfaces.delete(id);
      }
    }
    for (const id of rasterIds) {
      if (!this.surfaces.has(id)) this.surfaces.set(id, new RasterLayerSurface(id, this.size));
    }
  }

  public get(layerId: string): RasterLayerSurface | undefined {
    return this.surfaces.get(layerId);
  }

  public canAllocate(layerId: string): boolean {
    const surface = this.surfaces.get(layerId);
    if (!surface) return false;
    if (surface.isAllocated()) return true;
    const allocated = [...this.surfaces.values()].filter((candidate) =>
      candidate.isAllocated(),
    ).length;
    return canAllocateRasterSurface(this.size.width, this.size.height, allocated);
  }

  public canBeginStrokeWithPreview(layerId: string): boolean {
    const target = this.surfaces.get(layerId);
    if (!target) return false;
    const allocated = [...this.surfaces.values()].filter((surface) => surface.isAllocated()).length;
    return canBeginRasterStrokeWithPreview(
      this.size.width,
      this.size.height,
      allocated,
      target.isAllocated(),
    );
  }

  public canBeginSelectionWithPreview(layerId: string, regionBytes: number): boolean {
    const target = this.surfaces.get(layerId);
    if (!target || !Number.isSafeInteger(regionBytes) || regionBytes < 0) return false;
    const allocated = [...this.surfaces.values()].filter((surface) => surface.isAllocated()).length;
    const boundedRegionBytes = selectionLiveRegionBytes(regionBytes);
    if (!Number.isFinite(boundedRegionBytes)) return false;
    return canBeginRasterEditWithPreview(
      this.size.width,
      this.size.height,
      allocated,
      target.isAllocated(),
      boundedRegionBytes,
    );
  }

  public canApplyFill(layerId: string, clipPixelCount: number): boolean {
    const target = this.surfaces.get(layerId);
    if (!target) return false;
    const allocated = [...this.surfaces.values()].filter((surface) => surface.isAllocated()).length;
    return canApplyBoundedFill(
      this.size.width,
      this.size.height,
      allocated,
      target.isAllocated(),
      clipPixelCount,
    );
  }

  public samplePixel(layerId: string, x: number, y: number): RgbaTuple | null {
    if (x < 0 || y < 0 || x >= this.size.width || y >= this.size.height) return null;
    const context = this.surfaces.get(layerId)?.getCanvas()?.getContext("2d");
    if (!context) return [0, 0, 0, 0];
    const data = context.getImageData(Math.floor(x), Math.floor(y), 1, 1).data;
    const alpha = data[3] ?? 0;
    return alpha === 0 ? [0, 0, 0, 0] : [data[0] ?? 0, data[1] ?? 0, data[2] ?? 0, alpha];
  }

  public applyShapeCommand(command: Readonly<ShapeCommand>): boolean {
    const surface = this.surfaces.get(command.layerId);
    if (!surface || !this.canAllocate(command.layerId)) return false;
    renderShape(this.ensureContext(command.layerId), command);
    surface.markChanged();
    return true;
  }

  public applyFillCommand(command: Readonly<FillCommand>): FillResult {
    const clip = {
      x: Math.max(0, Math.ceil(command.clipRect.x)),
      y: Math.max(0, Math.ceil(command.clipRect.y)),
      width: Math.max(
        0,
        Math.floor(command.clipRect.x + command.clipRect.width) - Math.ceil(command.clipRect.x),
      ),
      height: Math.max(
        0,
        Math.floor(command.clipRect.y + command.clipRect.height) - Math.ceil(command.clipRect.y),
      ),
    };
    const pixelCount = clip.width * clip.height;
    const surface = this.surfaces.get(command.layerId);
    if (!surface || !this.canApplyFill(command.layerId, pixelCount)) {
      return { status: "rejected", filledPixels: 0 };
    }
    const context = this.ensureContext(command.layerId);
    const image = context.getImageData(clip.x, clip.y, clip.width, clip.height);
    const result = applyBoundedFloodFill(
      image,
      command.x - clip.x,
      command.y - clip.y,
      command.target,
      hexToRgba(command.replacementColour, command.replacementOpacity),
      command.tolerance,
    );
    if (result.status === "changed") {
      context.putImageData(image, clip.x, clip.y);
      surface.markChanged();
    }
    return result;
  }

  public canApplySelectionCommand(layerId: string, regionBytes: number): boolean {
    const target = this.surfaces.get(layerId);
    if (!target || !Number.isSafeInteger(regionBytes) || regionBytes < 0) return false;
    const allocated = [...this.surfaces.values()].filter((surface) => surface.isAllocated()).length;
    const targetAllocation = target.isAllocated() ? 0 : 1;
    const boundedRegionBytes = selectionLiveRegionBytes(regionBytes);
    if (!Number.isFinite(boundedRegionBytes)) return false;
    return canUseRasterSurfaceBudgetWithExtraBytes(
      this.size.width,
      this.size.height,
      allocated + targetAllocation,
      0,
      boundedRegionBytes,
    );
  }

  public captureSelection(
    layerId: string,
    rect: Readonly<IntegerRect>,
  ): ImmutableRasterPixelBuffer | null {
    const canvas = this.surfaces.get(layerId)?.getCanvas();
    const context = canvas?.getContext("2d");
    return context ? ImmutableRasterPixelBuffer.capture(context, rect) : null;
  }

  public applySelectionCommand(command: Readonly<RasterSelectionCommand>): boolean {
    const surface = this.surfaces.get(command.layerId);
    if (
      !surface ||
      !this.canApplySelectionCommand(command.layerId, command.sourcePixels.byteLength)
    )
      return false;
    const context = this.ensureContext(command.layerId);
    applyRasterSelectionCommandToContext(context, command);
    surface.markChanged();
    return true;
  }

  public ensureContext(layerId: string): CanvasRenderingContext2D {
    const surface = this.surfaces.get(layerId);
    if (!surface) throw new Error("The drawing layer no longer exists.");
    if (!this.canAllocate(layerId)) {
      throw new Error(
        "This tablet is low on drawing memory. Merge or clear a layer and try again.",
      );
    }
    return surface.ensureContext();
  }

  public async load(records: ReadonlyArray<StoredRasterLayerBlob>): Promise<void> {
    assertAggregateRasterMemoryWithinBudget(this.size.width, this.size.height, records);
    const byId = new Map(records.map((record) => [record.layerId, record]));
    for (const [id, surface] of this.surfaces) {
      await surface.load(byId.get(id) ?? { layerId: id, mimeType: "image/png", blank: true });
    }
  }

  public clone(): LayerRasterStore {
    const copy = new LayerRasterStore(this.size, []);
    for (const [id, surface] of this.surfaces) copy.surfaces.set(id, surface.clone());
    return copy;
  }

  public replaceWith(source: LayerRasterStore): void {
    this.destroy();
    for (const [id, surface] of source.surfaces) this.surfaces.set(id, surface.clone());
  }

  public clearLayer(layerId: string): void {
    this.surfaces.get(layerId)?.clear();
  }

  public clearPanel(panel: ComicPanel): void {
    for (const surface of this.surfaces.values()) {
      const context = surface.getCanvas()?.getContext("2d");
      if (context) {
        context.clearRect(panel.x, panel.y, panel.width, panel.height);
        surface.markChanged();
      }
    }
  }

  public duplicatePanel(source: ComicPanel, target: ComicPanel): void {
    for (const surface of this.surfaces.values()) {
      const canvas = surface.getCanvas();
      if (!canvas) continue;
      const context = surface.ensureContext();
      const temporary = document.createElement("canvas");
      temporary.width = Math.max(1, Math.ceil(source.width));
      temporary.height = Math.max(1, Math.ceil(source.height));
      const temporaryContext = temporary.getContext("2d");
      if (!temporaryContext) throw new Error("Canvas 2D is unavailable for panel duplication.");
      temporaryContext.drawImage(
        canvas,
        source.x,
        source.y,
        source.width,
        source.height,
        0,
        0,
        temporary.width,
        temporary.height,
      );
      context.clearRect(target.x, target.y, target.width, target.height);
      context.save();
      context.beginPath();
      context.rect(target.x, target.y, target.width, target.height);
      context.clip();
      context.drawImage(temporary, target.x, target.y, target.width, target.height);
      context.restore();
      temporary.width = 1;
      temporary.height = 1;
      surface.markChanged();
    }
  }

  public mergeDown(
    sourceId: string,
    targetId: string,
    sourceOpacity: number,
    targetOpacity: number,
  ): void {
    const source = this.surfaces.get(sourceId)?.getCanvas();
    const targetSurface = this.surfaces.get(targetId);
    if (!source && !targetSurface?.getCanvas()) return;
    if (!targetSurface) throw new Error("The merge target layer no longer exists.");
    if (!this.canMergeDown(sourceId, targetId)) {
      throw new Error("This tablet is low on drawing memory. Clear another layer before merging.");
    }

    // Build the complete result before touching the target. The preflight above
    // includes this one full-document transient surface in the same budget used
    // by load, import, and recovery.
    const temporary = document.createElement("canvas");
    temporary.width = this.size.width;
    temporary.height = this.size.height;
    const temporaryContext = temporary.getContext("2d");
    if (!temporaryContext) throw new Error("Canvas 2D is unavailable for layer merging.");
    const target = targetSurface.getCanvas();
    if (target) {
      temporaryContext.globalAlpha = targetOpacity;
      temporaryContext.drawImage(target, 0, 0);
    }
    if (source) {
      temporaryContext.globalAlpha = sourceOpacity;
      temporaryContext.drawImage(source, 0, 0);
    }
    const context = this.ensureContext(targetId);
    context.clearRect(0, 0, this.size.width, this.size.height);
    context.globalAlpha = 1;
    context.drawImage(temporary, 0, 0);
    temporary.width = 1;
    temporary.height = 1;
    targetSurface.markChanged();
  }

  public canMergeDown(sourceId: string, targetId: string): boolean {
    const source = this.surfaces.get(sourceId);
    const target = this.surfaces.get(targetId);
    if (!source || !target) return false;
    if (!source.isAllocated() && !target.isAllocated()) return true;
    const allocated = [...this.surfaces.values()].filter((surface) => surface.isAllocated()).length;
    const targetAllocation = target.isAllocated() ? 0 : 1;
    return canUseRasterSurfaceBudget(
      this.size.width,
      this.size.height,
      allocated + targetAllocation,
      1,
    );
  }

  public async encode(layers: ReadonlyArray<PersistedLayerState>): Promise<EncodedRasterLayer[]> {
    const records: EncodedRasterLayer[] = [];
    for (const layer of layers.filter((candidate) => candidate.type === "raster")) {
      const surface = this.surfaces.get(layer.id);
      if (!surface) throw new Error("A raster layer surface is missing.");
      records.push(await surface.encode());
    }
    return records;
  }

  public acknowledge(records: ReadonlyArray<EncodedRasterLayer>): void {
    for (const record of records) this.surfaces.get(record.layerId)?.acknowledge(record);
  }

  public destroy(): void {
    for (const surface of this.surfaces.values()) surface.destroy();
    this.surfaces.clear();
  }
}
